// My players' news at the top of My Team (DIRECTION U13, B-315): the latest
// stories about players on my roster, so an injury or a move is the first
// thing on the page that manages them. Absent when there is nothing from the
// last week, as the attention strip is when nothing waits on me: a calm roster
// has no box saying so.
//
// The same read as the news panel and the News screen (`GET …/news?filter=
// mine`), joined in flight rather than asked twice (`readStore.ts`).

import { valueOf } from '@fsp/domain';
import { useNews, useReaderZone } from '../../data/hooks';
import { useLeagueNow } from '../../data/demo';
import { StatusBadge } from '../../kit';
import { Button, Caption } from '../../kit/ui';
import { whenLabel } from '../NewsScreen';
import { PlayerName } from '../shared/Names';
import '../../styles/screens/news.css';

/** How far back a story still counts as news on My Team. */
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export function TeamNews({ shown = 3, onAll }: { shown?: number; onAll: () => void }) {
  const feed = useNews('mine');
  const zone = useReaderZone();
  // The league's moment: the demo league's held Tuesday, the wall's elsewhere.
  const now = useLeagueNow();
  const items = (valueOf(feed.snapshot) ?? [])
    .filter((n) => now - Date.parse(n.publishedAt) < WEEK_MS)
    .slice(0, shown);
  if (items.length === 0) return null;
  return (
    <section className="fsp-team-news" aria-label="Your players' news">
      <div className="fsp-team-news-head">
        <Caption>Your players’ news</Caption>
        <Button size="xs" variant="quiet" iconRight="next" onClick={onAll}>
          All news
        </Button>
      </div>
      {items.map((n) => (
        <div key={n.id} className="fsp-team-news-row">
          <span className="fsp-news-dot" data-severity={n.severity} aria-hidden />
          <div className="fsp-team-news-body">
            <span className="fsp-team-news-line">
              <span className="fsp-team-news-who">
                <PlayerName player={n.player} club={n.player.proTeam}>
                  {n.player.shortName}
                </PlayerName>
                <StatusBadge player={n.player} />
              </span>{' '}
              <span className="fsp-team-news-headline">{n.headline}</span>
            </span>
            <span className="fsp-sub fsp-team-news-when">
              {zone === null ? n.source : whenLabel(n.publishedAt, zone)}
            </span>
          </div>
        </div>
      ))}
    </section>
  );
}
